import { useEffect, useId, useRef, useState } from 'react';
import {
  cardLookupText,
  cardLookupHasMinimumConfirmedFields,
  type CardLookupField,
  type CardLookupRequest,
  type CardLookupResponse,
} from '@tcvs/shared';
import type { ApiResult } from '../auth/api-client';

export type CardLookupItem = CardLookupResponse['items'][number];
const FIELDS: Array<{ field: CardLookupField; label: string }> = [
  { field: 'category', label: 'Sport/category' },
  { field: 'year', label: 'Year' },
  { field: 'brand', label: 'Brand' },
  { field: 'number', label: 'Card number' },
  { field: 'name', label: 'Player or character name' },
  { field: 'product', label: 'Product or set' },
];
const COVERAGE: Record<NonNullable<CardLookupResponse['coverage']['reason']>, string> = {
  'search-budget': 'Only part of the available catalog was searched.',
  'result-limit': 'More matches may be available than shown.',
  'unavailable-source': 'Some checklist sources could not be searched.',
  'index-incomplete': 'Some checklists could not be searched.',
  'number-format':
    'Some number spellings may not have been searched. Try the printed card number if needed.',
};

export function CardLookupPanel({
  initialFields,
  categoryOptions = [],
  search,
  onApply,
  onClose,
  disabled = false,
}: {
  initialFields: CardLookupRequest['fields'];
  categoryOptions?: string[];
  search: (request: CardLookupRequest) => Promise<ApiResult<CardLookupResponse>>;
  onApply: (
    item: CardLookupItem,
    request: CardLookupRequest,
    stillCurrent: () => boolean,
  ) => Promise<void>;
  onClose: () => void;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();
  const [fields, setFields] = useState(initialFields);
  const [requiredFields, setRequiredFields] = useState<CardLookupField[]>([]);
  const [result, setResult] = useState<CardLookupResponse | null>(null);
  const [selected, setSelected] = useState<CardLookupItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [outdated, setOutdated] = useState(false);
  const sequence = useRef(0);
  const mounted = useRef(true);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const searchRef = useRef(search);
  searchRef.current = search;
  const request: CardLookupRequest = {
    fields: Object.fromEntries(
      FIELDS.flatMap(({ field }) =>
        cardLookupText(fields[field] ?? '') ? [[field, fields[field]!.trim()]] : [],
      ),
    ),
    requiredFields: requiredFields.filter((field) => cardLookupText(fields[field] ?? '')),
    limit: 20,
  };
  const confirmedFields = request.requiredFields ?? [];
  const canSearch = cardLookupHasMinimumConfirmedFields(request);
  const categories = Array.from(
    new Set(
      [fields.category ?? '', ...categoryOptions].map((value) => value.trim()).filter(Boolean),
    ),
  ).sort();
  const hasSearched = useRef(false);
  const invalidate = () => {
    sequence.current += 1;
    setOutdated(hasSearched.current);
    setResult(null);
    setSelected(null);
    setError(null);
    setLoading(false);
    setApplying(false);
  };
  const runSearch = async (input: CardLookupRequest) => {
    if (!canSearch || disabled) return;
    hasSearched.current = true;
    setOutdated(false);
    const token = ++sequence.current;
    setLoading(true);
    setApplying(false);
    setError(null);
    setResult(null);
    setSelected(null);
    try {
      const response = await searchRef.current(input);
      if (!mounted.current || sequence.current !== token) return;
      if (!response.ok) throw new Error(response.message ?? response.error);
      setResult(response.data);
    } catch (cause) {
      if (mounted.current && sequence.current === token)
        setError(
          cause instanceof Error ? cause.message : 'Search could not be completed. Try again.',
        );
    } finally {
      if (mounted.current && sequence.current === token) setLoading(false);
    }
  };
  useEffect(() => {
    if (disabled) {
      sequence.current += 1;
      setApplying(false);
      setLoading(false);
    }
  }, [disabled]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, []);
  const apply = async () => {
    if (selected === null || applying || disabled) return;
    const token = ++sequence.current;
    const stillCurrent = () =>
      mounted.current && !disabledRef.current && sequence.current === token;
    setApplying(true);
    setError(null);
    try {
      await onApply(selected, request, stillCurrent);
    } catch (cause) {
      if (stillCurrent())
        setError(
          cause instanceof Error ? cause.message : 'This match could not be applied. Search again.',
        );
    } finally {
      if (stillCurrent()) setApplying(false);
    }
  };
  return (
    <section className="tcvs-card-lookup" aria-label="Refine matches">
      <header>
        <strong>Refine matches</strong>
        <button
          type="button"
          className="tcvs-link-button"
          onClick={() => {
            invalidate();
            onClose();
          }}
        >
          Browse checklists
        </button>
      </header>
      <p className="tcvs-muted">
        Confirm the details you know with Must match. Editing a clue confirms it automatically;
        untouched clues are hints. Every confirmed detail must match. Search and preview leave your
        draft unchanged.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void runSearch(request);
        }}
      >
        {[FIELDS.slice(0, 3), FIELDS.slice(3)].map((group, index) => (
          <div
            className="tcvs-card-lookup-fields"
            key={index}
            role="group"
            aria-label={index === 0 ? 'Checklist details' : 'Card details'}
          >
            {group.map(({ field, label }) => (
              <div className="tcvs-card-lookup-field" key={field}>
                <label htmlFor={`${id}-${field}`}>{label}</label>
                <input
                  id={`${id}-${field}`}
                  value={fields[field] ?? ''}
                  autoComplete="off"
                  list={field === 'category' ? `${id}-categories` : undefined}
                  onChange={(event) => {
                    invalidate();
                    const value = event.target.value;
                    setFields((current) => ({ ...current, [field]: value }));
                    setRequiredFields((current) =>
                      cardLookupText(value)
                        ? Array.from(new Set([...current, field]))
                        : current.filter((item) => item !== field),
                    );
                  }}
                />
                {field === 'category' ? (
                  <datalist id={`${id}-categories`}>
                    {categories.map((category) => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                ) : null}
                <label className="tcvs-card-lookup-required">
                  <input
                    type="checkbox"
                    aria-label={`${label} must match`}
                    checked={requiredFields.includes(field)}
                    disabled={!cardLookupText(fields[field] ?? '')}
                    onChange={(event) => {
                      invalidate();
                      setRequiredFields((current) =>
                        event.target.checked
                          ? [...current, field]
                          : current.filter((item) => item !== field),
                      );
                    }}
                  />
                  Must match
                </label>
              </div>
            ))}
          </div>
        ))}
        <p className="tcvs-muted" id={`${id}-guidance`}>
          Confirm sport/category, year, brand and card number to search matching checklists. Full
          player name and product/set can narrow the matches further. A year includes matching
          seasons; brand matches complete words in product names. Card numbers ignore spaces and
          dashes, but must otherwise match exactly.
        </p>
        <div className="tcvs-card-lookup-actions">
          <button
            type="submit"
            disabled={!canSearch || disabled || loading}
            aria-describedby={`${id}-guidance`}
          >
            Find matches
          </button>
          <button
            type="button"
            className="tcvs-btn-secondary"
            onClick={() => {
              invalidate();
              setFields({});
              setRequiredFields([]);
            }}
          >
            Clear clues
          </button>
          {requiredFields.length > 0 ? (
            <button
              type="button"
              className="tcvs-link-button"
              onClick={() => {
                invalidate();
                setRequiredFields([]);
              }}
            >
              Remove required constraints
            </button>
          ) : null}
        </div>
      </form>
      <div role="status" aria-live="polite">
        {loading
          ? 'Searching imported cards…'
          : outdated
            ? 'Details changed. Find matches again to update the results.'
            : result?.needsMoreDetail
              ? 'More details are needed to finish the search.'
              : result
                ? `${result.items.length} matches for your confirmed details.`
                : !canSearch
                  ? 'Confirm sport/category, year, brand and card number to find matches.'
                  : 'Ready to find matches for your confirmed details.'}
      </div>
      {error !== null ? (
        <div role="alert">
          <p className="tcvs-error">{error}</p>
          <button
            type="button"
            disabled={!canSearch || disabled}
            onClick={() => void runSearch(request)}
          >
            Retry search
          </button>
        </div>
      ) : null}
      {result?.coverage.partial ? (
        <p className="tcvs-card-lookup-coverage">
          {result.coverage.reason
            ? COVERAGE[result.coverage.reason]
            : 'Search coverage is incomplete.'}{' '}
          Refine the clues to narrow the search.
        </p>
      ) : null}
      {result?.needsMoreDetail ? <p>{result.needsMoreDetail}</p> : null}
      {result?.items.length === 0 && !result.needsMoreDetail ? (
        <p>
          {result.coverage.partial
            ? 'No matches found in the portion searched. The search could not check every possible match.'
            : 'No matches found for your confirmed details.'}{' '}
          Review your clues or deliberately remove a Must match constraint to broaden the search.
        </p>
      ) : null}
      {result && result.items.length > 0 ? (
        <div className="tcvs-card-lookup-results">
          <label htmlFor={`${id}-matches`}>Matches for your confirmed details</label>
          <select
            id={`${id}-matches`}
            value={selected?.resultId ?? ''}
            disabled={disabled}
            onChange={(event) => {
              sequence.current += 1;
              setApplying(false);
              setSelected(
                result.items.find((item) => item.resultId === event.target.value) ?? null,
              );
              setError(null);
            }}
          >
            <option value="">Choose a match to preview…</option>
            {result.items.map((item) => (
              <option key={item.resultId} value={item.resultId}>
                {item.checklist.product.year} · {item.checklist.product.brand}{' '}
                {item.checklist.product.product} · #
                {item.entry.identity.number || item.entry.key.number}
                {' · '}
                {item.entry.identity.name || 'Name unavailable'} ·{' '}
                {item.entry.identity.subset || 'Subset not classified'}
                {item.entry.identity.variation ? ` · ${item.entry.identity.variation}` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {selected ? (
        <section className="tcvs-card-lookup-preview" aria-label="Selected match preview">
          <h4>
            {selected.entry.identity.name || 'Name unavailable'} · #
            {selected.entry.identity.number || selected.entry.key.number}
          </h4>
          <p>
            {selected.checklist.product.year} {selected.checklist.product.brand}{' '}
            {selected.checklist.product.product} ·{' '}
            {selected.entry.identity.subset || 'Subset not classified'}
            {selected.entry.identity.variation ? ` · ${selected.entry.identity.variation}` : ''}
          </p>
          <ul className="tcvs-card-lookup-matches">
            {selected.matchDetails.map((detail) => (
              <li key={detail.field} data-match={detail.status}>
                {confirmedFields.includes(detail.field) ? 'Must match: ' : 'Hint: '}
                {detail.explanation}
              </li>
            ))}
          </ul>
          {selected.entry.unresolvedClassification ? (
            <p>Some source classification is unresolved. Existing review safeguards still apply.</p>
          ) : null}
          <p>
            Use this match replaces the identity draft. Review the parallel before saving or
            verifying.
          </p>
          <button
            type="button"
            disabled={disabled || applying || loading}
            onClick={() => void apply()}
          >
            {applying ? 'Checking current match…' : 'Use this match'}
          </button>
        </section>
      ) : null}
    </section>
  );
}
